import OutOfContractPeriod from "@/components/common/OutOfContractPeriod";
import IPRestriction from "@/components/common/IPRestriction";
import { CustomButton } from "@/components/ui-elements/CustomButton";
import { LOGIN_PAGE_URL, MY_APP_URL, PASSWORD_SET_PAGE_URL } from "@/config";
import type { CustomAxiosError } from "@/lib/axios";
import { GetErrorCode } from "@/lib/axios";
import { ErrorCode } from "@/utils/errorCode";
import { Box, Link, Typography } from "@mui/material";
import { styled } from "@mui/system";
import NotFound from "@/components/common/404";
import type * as React from "react";

interface CustomErrorFallbackProps {
  error: CustomAxiosError;
}

const OopsWrapper = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  flexDirection: "column",
  minHeight: "100vh",
  backgroundColor: "#F0F0F0",
}));

const OopsTitle = styled(Typography)(() => ({
  color: "black",
  fontSize: "24px",
  fontWeight: "bold",
  marginTop: "254px",
}));

const OopsDescription = styled(Typography)(() => ({
  color: "black",
  fontSize: "16px",
  fontWeight: 500,
  marginTop: "70px",
  marginBottom: "70px",
}));

const OopsEmail = styled(Link)(() => ({
  color: "#255ACC",
  fontSize: "16px",
  fontWeight: 500,
  textDecoration: "none",
}));

const CustomErrorFallback: React.FC<CustomErrorFallbackProps> = ({ error }) => {
  const errorCode = GetErrorCode(error);
  const statusCode = error.response?.request.status;
  const isOutOfContractPeriod =
    statusCode === 403 &&
    (errorCode === ErrorCode.CONTRACT_EXPIRED ||
      errorCode === ErrorCode.CONTRACT_NOT_STARTED);
  const isPasswordExpired =
    statusCode === 401 && errorCode === ErrorCode.PASSWORD_EXPIRED;
  const isNotFound = statusCode === 404;
  const isIPRestricted =
    statusCode === 403 && errorCode === ErrorCode.IP_ACCESS_DENIED;
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const isNetworkError = error.code === "ERR_NETWORK";

  if (isIPRestricted) {
    return <IPRestriction />;
  }

  if (isNotFound) {
    return <NotFound />;
  }

  if (isOutOfContractPeriod) {
    return <OutOfContractPeriod />;
  }

  if (isPasswordExpired) {
    window.location.replace(
      `${PASSWORD_SET_PAGE_URL}?redirect=${MY_APP_URL}/views/`
    );
    return null;
  }

  // MEMO: 認証系エラー(401,403)の場合はログイン画面にリダイレクトするため、エラーページを表示しない
  // IP制限エラーは既に上部で処理されているため、ここでは対象外
  if ((statusCode === 401 || statusCode === 403) && !isIPRestricted) {
    window.location.replace(`${LOGIN_PAGE_URL}?redirect=${MY_APP_URL}/views/`);
    return null;
  }

  const handleOnClick = () => {
    window.open(
      "https://share.hsforms.com/1WnG0yykMTrekU398-NXLwAns1gl",
      "_blank"
    );
  };

  return (
    <OopsWrapper>
      <OopsTitle variant="h1">
        {isNetworkError
          ? "ネットワークエラーが発生しています"
          : "予期せぬエラーが発生しています"}
      </OopsTitle>
      {isNetworkError ? (
        <OopsDescription variant="body1">
          ネットワークの接続状況をご確認の上、
          <br />
          画面を更新するか、しばらく時間をおいて再度お試しください。
        </OopsDescription>
      ) : (
        <OopsDescription variant="body1">
          大変お手数ではございますが、
          <br />
          以下お問い合わせフォーム、 メール（
          <OopsEmail href="mailto:cs@recerqa.com">cs@recerqa.com</OopsEmail>）
          <br />
          又はお電話（03-6823-4784）にてお問い合わせください。
        </OopsDescription>
      )}

      {isNetworkError ? (
        <CustomButton
          label="画面を更新する"
          onClick={() => {
            window.location.reload();
          }}
          size="md"
          sx={{ fontSize: "14px" }}
        />
      ) : (
        <CustomButton
          label="お問い合わせ"
          onClick={() => handleOnClick()}
          size="md"
          sx={{ fontSize: "14px" }}
        />
      )}
    </OopsWrapper>
  );
};

export default CustomErrorFallback;
